import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js/min';

/**
 * Validação de nome completo.
 * Aceita letras (com acentos), espaços, apóstrofo e hífen. Bloqueia números e símbolos.
 */
const NAME_REGEX = /^[A-Za-zÀ-ÖØ-öø-ÿ'-]+(?: [A-Za-zÀ-ÖØ-öø-ÿ'-]+)*$/;

export function isValidName(name: string): boolean {
    const value = name.trim();
    if (value.length < 2 || value.length > 80) return false;
    return NAME_REGEX.test(value);
}

/**
 * Validação de e-mail alinhada à especificação do WHATWG (a mesma usada pelo
 * <input type="email"> dos navegadores) — rigorosa o suficiente para rejeitar
 * a grande maioria dos e-mails malformados, sem cair nos exageros da RFC 5322 completa.
 */
const EMAIL_REGEX =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/;

export function isValidEmail(email: string): boolean {
    const value = email.trim();
    if (!value || value.length > 254) return false;
    if (!EMAIL_REGEX.test(value)) return false;

    const [localPart, domain] = value.split('@');
    if (localPart.length > 64) return false;
    if (localPart.includes('..') || domain.includes('..')) return false;

    // Exige um TLD com pelo menos 2 letras (rejeita "usuario@dominio").
    const tld = domain.split('.').pop() ?? '';
    return /^[a-zA-Z]{2,}$/.test(tld);
}

/**
 * Validação de telefone/WhatsApp com `libphonenumber-js`, a mesma biblioteca usada
 * pelo Google libphonenumber. Em vez de apenas contar dígitos, ela confere se o DDI
 * informado existe e se a quantidade de dígitos é compatível com o plano de
 * numeração real daquele país.
 */
export function isValidPhone(digitsOnly: string): boolean {
    const digits = digitsOnly.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 15) return false;

    const phoneNumber = parsePhoneNumberFromString(`+${digits}`);
    return phoneNumber?.isValid() ?? false;
}

export function formatPhoneAsYouType(digitsOnly: string): string {
    const digits = digitsOnly.replace(/\D/g, '');
    if (!digits) return '';
    return new AsYouType().input(`+${digits}`);
}

/** Requisitos de senha exibidos no checklist em tempo real. */
export interface PasswordRequirement {
    id: 'minLength' | 'uppercase' | 'lowercase' | 'number' | 'special';
    labelKey: string;
    test: (password: string) => boolean;
}

export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
    { id: 'minLength', labelKey: 'register.senhaReqMinLength', test: (p) => p.length >= 8 },
    { id: 'uppercase', labelKey: 'register.senhaReqUppercase', test: (p) => /[A-Z]/.test(p) },
    { id: 'lowercase', labelKey: 'register.senhaReqLowercase', test: (p) => /[a-z]/.test(p) },
    { id: 'number', labelKey: 'register.senhaReqNumber', test: (p) => /\d/.test(p) },
    { id: 'special', labelKey: 'register.senhaReqSpecial', test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export function isPasswordStrongEnough(password: string): boolean {
    return PASSWORD_REQUIREMENTS.every((req) => req.test(password));
}

export type PasswordStrengthLevel = 'empty' | 'weak' | 'medium' | 'strong';

export function getPasswordStrengthLevel(password: string): PasswordStrengthLevel {
    if (!password) return 'empty';
    const metCount = PASSWORD_REQUIREMENTS.filter((req) => req.test(password)).length;
    if (metCount <= 1) return 'weak';
    if (metCount <= 3) return 'medium';
    return 'strong';
}